/**
 * Issue-only health rail summary (S22) — derives a minimal "what needs
 * attention" digest PURELY from S3 runtime state + S12 alert state. Pure, no
 * store access (the component owns subscriptions and builds the input array).
 *
 * Segments:
 *  - `stale`: a query-backed tile currently in `runtime.status === 'stale'`
 *    (either S3 reason — `connection-lost` OR `policy-blocked`; the rail
 *    doesn't distinguish, both mean "not showing fresh data right now").
 *    Genuine query errors (`status === 'error'`) are deliberately NOT counted
 *    here — they already show a persistent red badge on the tile itself
 *    (`TileStatusBadge`), whereas staleness is easy to miss at a glance
 *    across a big dashboard. This rail exists for the quiet issues.
 *  - `slow`: a CURRENTLY SUCCEEDING tile whose last run took > 5x the rolling
 *    median of its own recent runs — the SAME guardrail math the Add Tile
 *    trust strip uses (`components/add-tile/TrustStrip.tsx`), reused
 *    verbatim rather than forked. A tile that's stale/erroring is already
 *    flagged there; its stale duration history doesn't also count as "slow".
 *  - `alert`: a stat tile whose persisted `TileAlertState.status === 'firing'`
 *    (S12) — an armed-but-not-crossed alert is healthy, not an issue.
 */
import { median, REFRESH_GUARDRAIL_FACTOR } from '../utils/durationGuardrail';
import type { DashboardTileRuntime } from '../types/dashboardRuntime';
import type { TileAlertState } from '../types/dashboardTypes';

export type HealthSegment = 'stale' | 'slow' | 'alert';

/** Per-tile input the component assembles from the runtime + alert stores. */
export type HealthRailTileInput = {
  tileId: string;
  runtimeStatus?: DashboardTileRuntime['status'];
  durationsMs?: DashboardTileRuntime['durationsMs'];
  alertStatus?: TileAlertState['status'];
};

export type HealthSegmentSummary = { segment: HealthSegment; count: number; tileIds: string[] };

export type HealthSummary = {
  /** True iff there is nothing to show — the rail renders NOTHING then. */
  isHealthy: boolean;
  /** Only non-zero segments, in the canonical stale → slow → alert order. */
  segments: HealthSegmentSummary[];
};

const SEGMENT_ORDER: HealthSegment[] = ['stale', 'slow', 'alert'];

/**
 * A tile is "slow" when its LAST duration exceeds `REFRESH_GUARDRAIL_FACTOR`
 * (5x) the rolling median of its own recent durations — mirrors the trust
 * strip's `refresh.seconds*1000 < medianMs * FACTOR` check, but compares the
 * tile's actual last run to its own history instead of a configured
 * interval. Needs at least one duration; a lone sample's median equals
 * itself so it can never trip (1x is never > 5x).
 */
export function isTileSlow(durationsMs: number[] | undefined): boolean {
  if (!durationsMs || durationsMs.length === 0) return false;
  const last = durationsMs[durationsMs.length - 1];
  const med = median(durationsMs);
  return med > 0 && last > med * REFRESH_GUARDRAIL_FACTOR;
}

export function computeHealthSummary(tiles: HealthRailTileInput[]): HealthSummary {
  const byTileIds: Record<HealthSegment, string[]> = { stale: [], slow: [], alert: [] };

  for (const tile of tiles) {
    if (tile.runtimeStatus === 'stale') {
      byTileIds.stale.push(tile.tileId);
    } else if (tile.runtimeStatus === 'success' && isTileSlow(tile.durationsMs)) {
      byTileIds.slow.push(tile.tileId);
    }
    if (tile.alertStatus === 'firing') {
      byTileIds.alert.push(tile.tileId);
    }
  }

  const segments = SEGMENT_ORDER.map((segment) => ({
    segment,
    count: byTileIds[segment].length,
    tileIds: byTileIds[segment],
  })).filter((s) => s.count > 0);

  return { isHealthy: segments.length === 0, segments };
}

/**
 * Tile ids belonging to one segment — `[]` when that segment isn't present
 * (zero-count segments are dropped from `segments`). Backs the rail's
 * "Refresh stale" bulk action, which needs the stale tiles' identities without
 * re-deriving the summary.
 */
export const segmentTileIds = (summary: HealthSummary, segment: HealthSegment): string[] =>
  summary.segments.find((s) => s.segment === segment)?.tileIds ?? [];
